import React, { useEffect, useState, useRef } from 'react';
import { Container, Text } from '@weareconceptstudio/core';
import PreloaderStyle from './style';
import { withDataContext } from '@/context';
import { elemA, elemV, elemG, elemY, elemN } from './utils';

const Preloader = ({ disablePreloader, isPreloaderActive, isHomePortfolio }) => {
	//! If you make a change, also change it in "Infinite Item & Infinite Scroll Append".
	const loadCount = isHomePortfolio ? 12 : false;

	//! Refs
	const requestRef = useRef();
	const displayPercentRef = useRef(0);
	const imagesLoadedRef = useRef(false);

	//! States
	const [realPercent, setRealPercent] = useState(0);
	const [displayPercent, setDisplayPercent] = useState(0);

	//! Load images and track progress
	useEffect(() => {
		let images = Array.from(document.images);

		if (loadCount && loadCount > 0 && loadCount <= images.length) {
			images = images.slice(0, loadCount);
		}

		let loadedCount = 0;

		if (images.length === 0) {
			setRealPercent(100);
			imagesLoadedRef.current = true;
			return;
		}

		const handleLoad = () => {
			loadedCount++;
			const percent = Math.round((loadedCount / images.length) * 100);
			setRealPercent(percent);

			if (loadedCount === images.length) {
				imagesLoadedRef.current = true;
			}
		};

		images.forEach((img) => {
			if (img.complete && img.naturalHeight !== 0) {
				handleLoad();
			} else {
				img.addEventListener('load', handleLoad);
				img.addEventListener('error', handleLoad);
			}
		});

		return () => {
			images.forEach((img) => {
				img.removeEventListener('load', handleLoad);
				img.removeEventListener('error', handleLoad);
			});
		};
	}, [loadCount]);

	//! Animate displayPercent toward realPercent
	useEffect(() => {
		const animate = () => {
			const delta = realPercent - displayPercentRef.current;
			if (Math.abs(delta) < 1) {
				setDisplayPercent(realPercent);
				cancelAnimationFrame(requestRef.current);
				return;
			}

			displayPercentRef.current += delta * 0.1;
			const rounded = Math.round(displayPercentRef.current);
			setDisplayPercent(rounded);
			requestRef.current = requestAnimationFrame(animate);
		};

		requestRef.current = requestAnimationFrame(animate);
		return () => cancelAnimationFrame(requestRef.current);
	}, [realPercent]);

	//! Disable preloader when done
	useEffect(() => {
		let timeoutId;
		const preventScroll = (e) => e.preventDefault();

		window.addEventListener('wheel', preventScroll, { passive: false });
		window.addEventListener('touchmove', preventScroll, { passive: false });

		document.documentElement.classList.add('touch-action');
		document.body.style.overflow = 'hidden';
		document.body.style.touchAction = 'none';

		const shouldHide = imagesLoadedRef.current && displayPercent === 100;

		if (shouldHide) {
			timeoutId = setTimeout(() => {
				window.removeEventListener('wheel', preventScroll);
				window.removeEventListener('touchmove', preventScroll);

				document.documentElement.classList.remove('touch-action');
				document.body.style.overflow = '';
				document.body.style.touchAction = '';

				disablePreloader();
			}, 800);
		}

		return () => {
			clearTimeout(timeoutId);

			window.removeEventListener('wheel', preventScroll);
			window.removeEventListener('touchmove', preventScroll);

			document.body.style.overflow = '';
			document.body.style.touchAction = '';
			document.documentElement.classList.remove('touch-action');
		};
	}, [imagesLoadedRef.current, displayPercent]);

	//! Logo animation retrigger interval
	useEffect(() => {
		if (!imagesLoadedRef.current) {
			const wrapper = document.querySelector('.preloader-wrapper');

			const interval = setInterval(() => {
				wrapper?.classList.remove('retrigger');
				void wrapper?.offsetWidth; //! Force reflow
				wrapper?.classList.add('retrigger');
			}, 2500);

			return () => clearInterval(interval);
		}
	}, [imagesLoadedRef.current]);

	return (
		<PreloaderStyle className={`preloader-wrapper ${!isPreloaderActive ? 'closed-load-anim' : ''}`}>
			<Container className='container-wrapper'>
				<div className='preloader-inner-wrapper'>
					<div className='logo-animation-block'>
						<div className='logo-animation-inner-block'>
							{[elemA, elemV, elemA, elemG, elemY, elemA, elemN].map((elem, i) => (
								<div
									key={i}
									className='elem-block'
								>
									{elem} {elem}
								</div>
							))}
						</div>
						<div className='load-wrapper'>
							<div className='load-inner-wrapper'>
								<Text className='font-neue-roman h6 white-1000 align-center'>{Math.round(displayPercent)}%</Text>
							</div>
						</div>
					</div>
				</div>
			</Container>
		</PreloaderStyle>
	);
};

export default withDataContext(Preloader, ['disablePreloader']);
